import { on } from 'cadenza/utils/event-util';
import {
  PostMessageApi,
  MessageTypes,
  windowEndpoint
} from 'cadenza/utils/post-message/post-message-api';
import { cadenzaTopWindow } from 'cadenza/utils/iframe';

/**
 * Post-Message API for clients (often iframes) to communicate with the server api (often called the parent, because it contains the iframe).
 *
 * In this context server is not meant as server side (backend), but rather the counterparty of a client api.
 * The server is just the corresponding post message api associated with a window object which the client post message communicates with.
 * Check the documentation in server-post-message-api.js too, since a client-post-message-api is used in conjunction with the server api.
 *
 * The server window creates a new message channel with 2 ports, one of them is kept in the server api, and one is sent here.
 * For this initialization step we're listening for ONE INIT event on the iframe object, since it supports postMessage
 * (see the INIT listener in the constructor with once = true). Once this INIT event has arrived, we get the port
 * sent from the server-post-message-api (_switchToNewMessageChannelIfNecessary) and from now on we will only use this port for a 1 to 1
 * communication with the server window.
 *
 * Ignores requests until connected to a server window.
 *
 * @fires MessageTypes#INIT
 */
export class ClientPostMessageApi extends PostMessageApi {

  _hasInitializedPortEndpoint;
  #parentInitParams; // Parameters received from the server into the iframe
  #initParams; // Parameters which we will send to the server once we received the INIT

  /**
   * Creates a new instance of a client api to exchange messages with the server api
   *
   * @param {object} options - Additional options.
   * @param {object} [options.initParams] - parameters which we send to the server upon initialization
   * @param {import('cadenza/utils/post-message/post-message-api').Endpoint} [options.endpoint] -
   * The endpoint of the API, defaults to windowEndpoint(window.parent)
   */
  constructor ({ initParams = {}, endpoint = windowEndpoint(window.parent) } = {}) {
    super();
    this.#initParams = initParams;
    this.endpoint = endpoint;

    /**
     * This listener listens on the client (often iframe) object ONCE, in order to receive the endpoint sent by the server api.
     * Once it received the message channel port (called endpoint here) it uses this for communication with the server api
     * Finally we're responding with another INIT message to the parent window containing the initParams (see init method below)
     */
    on(this, MessageTypes.INIT, event => {
      const newEndpoint = event.detail.endpoint;
      if (newEndpoint) {
        this.endpoint = newEndpoint; // Switch to the port sent from the server api.
      }
      this._hasInitializedPortEndpoint = true;
      this.#parentInitParams = event.detail.parentInitParams;
      this.init();
    }, { once: true });

    document.documentElement.classList.add('is-partial');
    // For embedded content, the view is activated from the similar event listener in view.js::connectedCallback
    if (window !== cadenzaTopWindow) {
      on(window, [ 'focusin', 'click', 'mousedown', 'touchstart' ], (event) => {
        if (event.type !== 'click' || (event.screenX && event.screenY)) {
          // Activate the view only for user clicks and not for programmatic ones.
          // We can ignore click events, which were triggered via keyboard,
          // because that should be covered already by "focusin".
          this.activate();
          // Special case to manually trigger the click event on the selection-cart--action which is not triggered
          // when the view is not active. It is because mousedown/touchstart event changes the position of the
          // selection cart by making the toolbar and legend visible.
          // So, we trigger the click event only in such case where click event is not triggered
          if (event.target?.classList.contains('selection-cart--action')
            && (event.type === 'mousedown' || event.type === 'touchstart')
            && event.target.closest('d-selection-cart').style.top === '0px') { // when toolbar is not shown
            event.target.click();
          }
        }
      });
    }
  }

  /**
   * Sends out a message using a request-response pattern. Times out after TIMEOUT_MS.
   *
   * @param {object} msg - The message to be sent.
   * @param {string} msg.type - The event type used to dispatch events with.
   * @param {*} [msg.detail] - Additional data sent with the message.
   * @param {boolean} [msg.cancelable] - When set to true (default: false) the event on the
   *   receiver side can cancel the request using event#preventDefault().
   * @param {Transferable[]} [transferables] - Transferable objects to be transferred — these
   *   objects have their ownership transferred to the receiving browsing context, so are no longer
   *   usable by the sending browsing context.
   * @return {Promise.<boolean|NoEndpointDefinedError>} A promise
   *   reflecting the receiving of the request on iframe content side. First argument states the
   *   cancellation of the request by the recipient via event#preventDefault().
   */
  request (msg, transferables = undefined) {
    if (!this._hasInitializedPortEndpoint) {
      // Postpone request until connected
      return new Promise((resolve) =>
        on(this, MessageTypes.INIT, () => resolve(), { once: true }))
        .then(() => super.request(msg, transferables));
    }
    return super.request(msg, transferables);
  }

  init () {
    return this.request({ type: MessageTypes.INIT, detail: this.#initParams });
  }

  activate () {
    return this.request({ type: MessageTypes.ACTIVATE });
  }

  get parentInitParams () {
    return this.#parentInitParams;
  }

  reload (detail) {
    return this.request({ type: MessageTypes.RELOAD, detail, cancelable: true });
  }

}
